import { useMemo, useState } from 'react'

import moment from 'moment'

import { Typography } from '@material-ui/core'

import { IConversion } from 'store/modules/report/types'

import getConversionInsight from './conversionInsight'

import {
  ChartAverage,
  ChartBar,
  ChartBody,
  ChartFooter,
  ChartPlot,
  ChartSubtitle,
  ChartXAxis,
  ChartYAxis
} from './styles'

const ABOVE_COLOR = '#1f2fa8'
const BELOW_COLOR = '#8f98e0'

const GRID_STEPS = [0, 20, 40, 60, 80, 100]
const GRID_DIVISIONS = GRID_STEPS.length - 1

interface ConversionByHourProps {
  conversion: IConversion
  range: {
    from: string | Date
    to: string | Date
  }
}

const formatNumber = (value: number) => value.toLocaleString('pt-BR')

const formatShare = (value: number) =>
  `${value.toFixed(1).replace('.', ',')}%`

// Arredonda o topo do eixo para um valor "redondo" acima do maior fluxo.
//
// O resultado é sempre múltiplo de 5 e no mínimo 5, porque o eixo é dividido em
// cinco faixas: sem isso, um dia de fluxo baixo (topo 2, por exemplo) gera
// divisões fracionadas que, arredondadas, repetem rótulo — 2, 2, 1, 1, 0, 0.
const niceCeil = (value: number) => {
  if (value <= 0) return 5

  const magnitude = 10 ** Math.floor(Math.log10(value))
  const rounded = Math.ceil(value / (magnitude / 2)) * (magnitude / 2)

  return Math.max(5, Math.ceil(rounded / GRID_DIVISIONS) * GRID_DIVISIONS)
}

// As plataformas de contagem devolvem também as horas antes de abrir e depois
// de fechar, zeradas. Elas só poluem o gráfico e estragam o "menor fluxo", mas
// um zero no meio do expediente é informação — então corta só as pontas.
const trimEmptyEdges = (hours: IConversion['hours']) => {
  let start = 0
  let end = hours.length - 1

  while (start <= end && hours[start].visitors === 0) start++
  while (end >= start && hours[end].visitors === 0) end--

  return hours.slice(start, end + 1)
}

const ConversionByHour = ({ conversion, range }: ConversionByHourProps) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  const hours = useMemo(
    () => trimEmptyEdges(conversion.hours),
    [conversion.hours]
  )

  const summary = useMemo(() => {
    const total = hours.reduce((acc, hour) => acc + hour.visitors, 0)
    const sorted = [...hours].sort((a, b) => b.visitors - a.visitors)

    return {
      total,
      average: hours.length ? total / hours.length : 0,
      best: sorted[0],
      worst: sorted[sorted.length - 1],
      scale: niceCeil(sorted.length ? sorted[0].visitors : 0)
    }
  }, [hours])

  if (!hours.length) {
    return (
      <Typography variant='subtitle2'>
        Ainda não há contagem de pessoas para o período selecionado.
      </Typography>
    )
  }

  const share = (visitors: number) =>
    summary.total > 0 ? (visitors / summary.total) * 100 : 0

  const highlighted = hoveredIndex !== null ? hours[hoveredIndex] : summary.best

  const insight = getConversionInsight(hours)

  const period = `${moment(range.from).format('DD/MM')} a ${moment(
    range.to
  ).format('DD/MM')}`

  const averageOffset = summary.scale
    ? (summary.average / summary.scale) * 100
    : 0


  return (
    <>
      <ChartSubtitle>
        <span>
          Pessoas que entraram na loja, somadas por horário &middot; {period}
        </span>
        <span>
          <strong className='hour'>{highlighted.hour}h</strong> &middot;{' '}
          <strong className='metric'>
            {formatNumber(highlighted.visitors)} pessoas
          </strong>{' '}
          &middot; {formatShare(share(highlighted.visitors))} do fluxo
        </span>
      </ChartSubtitle>

      <ChartBody>
        <ChartYAxis>
          {GRID_STEPS.map(step => (
            <span key={step} style={{ top: `${step}%` }}>
              {formatNumber(Math.round((summary.scale * (100 - step)) / 100))}
            </span>
          ))}
        </ChartYAxis>

        <ChartPlot>
          {GRID_STEPS.map(step => (
            <div
              key={step}
              className={step === 100 ? 'grid base' : 'grid'}
              style={{ top: `${step}%` }}
            />
          ))}

          <div className='average' style={{ bottom: `${averageOffset}%` }} />

          <div className='bars' onMouseLeave={() => setHoveredIndex(null)}>
            {hours.map((hour, index) => {
              const active = hoveredIndex === index

              return (
                <ChartBar
                  key={hour.hour}
                  active={active}
                  onMouseEnter={() => setHoveredIndex(index)}
                >
                  <span className='value'>{formatNumber(hour.visitors)}</span>
                  <div
                    className='bar'
                    style={{
                      height: `${
                        summary.scale ? (hour.visitors / summary.scale) * 100 : 0
                      }%`,
                      background:
                        hour.visitors >= summary.average
                          ? ABOVE_COLOR
                          : BELOW_COLOR
                    }}
                  />
                </ChartBar>
              )
            })}
          </div>
        </ChartPlot>

        {/* O rótulo da média mora fora da área das barras, para não precisar de
            fundo nem brigar por espaço com a barra que estiver ali. */}
        <ChartAverage>
          <span style={{ bottom: `${averageOffset}%` }}>
            média {formatNumber(Math.round(summary.average))}
          </span>
        </ChartAverage>
      </ChartBody>

      <ChartXAxis>
        {hours.map(hour => (
          <div className='tick' key={hour.hour}>
            <span className='hour'>{hour.hour}h</span>
            <span className='detail'>
              {formatShare(share(hour.visitors))} do fluxo
            </span>
          </div>
        ))}
      </ChartXAxis>

      <ChartFooter>
        <div className='item'>
          <span className='title'>MÉDIA POR HORÁRIO</span>
          <span className='value'>
            {formatNumber(Math.round(summary.average))} pessoas{' '}
            <span className='soft'>
              &middot; {formatNumber(summary.total)} no total
            </span>
          </span>
        </div>
        <div className='item'>
          <span className='title'>MAIOR FLUXO</span>
          <span className='value'>
            {summary.best.hour}h &middot;{' '}
            <span className='best'>{formatNumber(summary.best.visitors)}</span>
          </span>
        </div>
        <div className='item'>
          <span className='title'>MENOR FLUXO</span>
          <span className='value'>
            {summary.worst.hour}h &middot;{' '}
            <span className='worst'>{formatNumber(summary.worst.visitors)}</span>
          </span>
        </div>
        {insight && (
          <div className='item'>
            <span className='title'>PADRÃO HORÁRIO</span>
            <span className='hint'>{insight}</span>
          </div>
        )}
      </ChartFooter>
    </>
  )
}

export default ConversionByHour
